'use client';

import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useSession } from 'next-auth/react';

// Widened to support any 2-letter locale code dynamically
export type SupportedLang = string;

export interface AppContextType {
  lang: SupportedLang;
  setLang: (lang: SupportedLang) => void;
  /** Lower-cased role string for nav/layout switching. Derived from NextAuth session. */
  role: 'freelancer' | 'recruiter' | 'admin';
}

const defaultContext: AppContextType = {
  lang: 'pt',
  setLang: () => {},
  role: 'freelancer',
};

export const AppContext = createContext<AppContextType>(defaultContext);

/** Convenience hook for language access */
export function useLanguage() {
  const { lang, setLang } = useContext(AppContext);
  return { lang, setLang };
}

function sessionRoleToContextRole(role?: string): 'freelancer' | 'recruiter' | 'admin' {
  if (role === 'RECRUITER') return 'recruiter';
  if (role === 'ADMIN' || role === 'SUB_ADMIN') return 'admin';
  return 'freelancer';
}

const STORAGE_KEY = 'autonomo_lang';
const COOKIE_KEY  = 'autonomo_lang';

function readPersistedLang(): SupportedLang {
  // Try localStorage first (survives soft-navigation), then cookie
  try {
    const ls = localStorage.getItem(STORAGE_KEY);
    if (ls && /^[a-z]{2}$/.test(ls)) return ls;
  } catch { /* SSR */ }

  try {
    const match = document.cookie.match(new RegExp(`${COOKIE_KEY}=([a-z]{2})`));
    if (match && /^[a-z]{2}$/.test(match[1])) return match[1];
  } catch { /* SSR */ }

  return 'pt';
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const [lang, setLangState] = useState<SupportedLang>('pt');

  // Initialise from storage on mount (client-side only)
  useEffect(() => {
    setLangState(readPersistedLang());
  }, []);

  // If the session has a preferredLanguage field, respect it (overrides local pref)
  useEffect(() => {
    const prefLang = (session?.user as Record<string, unknown> | undefined)?.preferredLanguage as string | undefined;
    if (prefLang && /^[a-z]{2}$/.test(prefLang)) {
      setLangState(prefLang);
    }
  }, [session]);

  // Listen for storage events so tabs stay in sync
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && /^[a-z]{2}$/.test(e.newValue)) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setLang = (newLang: SupportedLang) => {
    setLangState(newLang);
    // Persist in both localStorage (for soft-nav) and cookie (for SSR/middleware)
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch { /* SSR */ }
    document.cookie = `${COOKIE_KEY}=${newLang};path=/;max-age=31536000;SameSite=Lax`;
  };

  const role = sessionRoleToContextRole(session?.user?.role as string | undefined);

  return (
    <AppContext.Provider value={{ lang, setLang, role }}>
      {children}
    </AppContext.Provider>
  );
}
